import React from "react";
import ReactDOM from 'react-dom/client'
import App from './App.jsx'
// ui-blocks first: its TipTap reset carries a `:root *` rule setting
// `transition-property: none`. That ties with every Tailwind transition utility
// on specificity, so importing it after Tailwind silently disabled every CSS
// transition in the app. Loading it first lets the utilities win.
import '@strivelabs/ui-blocks/styles.css'; // Import TipTap editor styles
import './index.css'
import '@strivelabs/ui-core/styles/v2-tokens.css'; // shadcn tokens for the v2 family
import { ClerkProvider } from '@clerk/clerk-react'
import { PostHogProvider } from 'posthog-js/react'
import type { PostHogConfig } from 'posthog-js'
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import '@fontsource/inter/400.css'; // Regular weight
import '@fontsource/inter/500.css'; // Medium weight
import '@fontsource/inter/600.css'; // Semi-bold
import '@fontsource/inter/700.css'; // Bold
import '@fontsource/fraunces/400.css'; // Fraunces font
import { ErrorBoundary } from '@/components/common';
import { logger, logInfo } from '@/lib/logger';
import { getDomainConfig } from '@/config/domainConfig';
import { beforeSend, getEnvironment } from '@/lib/analytics';
// @demo: added for demo purposes
import { Toaster } from '@strivelabs/ui-core';

const env = import.meta.env.VITE_APP_NODE_ENV;

const isProduction = env === "production";

// One PostHog project serves every environment — they're separated by the
// `environment` super property, which dashboards filter on.
//
// Ingestion goes through our managed reverse proxy (k.strivelabs.ai), so
// analytics requests are first-party and survive ad-blockers. The direct
// ingestion host stays as the fallback: if the env var is unset — or the proxy
// isn't provisioned yet — events still land rather than silently disappearing.
const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';

// Where the PostHog *app* lives, as opposed to where events are sent. The
// proxy only fronts ingestion, so without this the toolbar and the "view in
// PostHog" links would resolve against k.strivelabs.ai and break. This tracks
// the account's region (US Cloud), not the deploy environment, so it's a
// constant rather than an env var.
const POSTHOG_UI_HOST = 'https://us.posthog.com';

const options: Partial<PostHogConfig> = {
  api_host: import.meta.env.VITE_APP_PUBLIC_POSTHOG_HOST || DEFAULT_POSTHOG_HOST,
  ui_host: POSTHOG_UI_HOST,
  // '2025-05-24' turns on SPA pageviews (capture_pageview: 'history_change')
  // and person_profiles: 'identified_only'. Without it PostHog only sees one
  // pageview per hard load, which is useless for a client-routed app.
  defaults: '2025-05-24',
  disable_session_recording: !isProduction,
  // Drops demo-surface events and stamps route_pattern. See lib/analytics.
  before_send: beforeSend,
  loaded: (posthog) => {
    posthog.register({ environment: getEnvironment() });
  },
};

// Clerk publishable key is resolved from the hostname via domainConfig
const PUBLISHABLE_KEY = getDomainConfig().clerkPublishableKey;

// Enhanced QueryClient with global error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        // Check if error has a status code
        if (typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number') {
          // Always retry 401 errors (up to 3 times) to allow Clerk token refresh
          // This handles cases where the user returns to a stale tab
          if (error.status === 401) {
            return failureCount < 3;
          }
          // Don't retry on other 4xx errors (client errors)
          if (error.status >= 400 && error.status < 500) {
            return false;
          }
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      // Add retry delay for exponential backoff, helps with 401 retries
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      // Prevent automatic refetching when switching tabs
      // This is critical for chat sessions - we don't want to disrupt ongoing streams
      refetchOnWindowFocus: false,
      // Set stale time to prevent unnecessary refetches
      // Data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
    },
    mutations: {
      onError: (error) => {
        logger(error instanceof Error ? error : new Error(String(error)), {
          context: 'React Query - Mutation Error',
          type: 'mutation_error',
        });
      },
    },
  },
});

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Publishable Key")
}

// Log app initialization
logInfo('Application starting', { 
  mode: import.meta.env.MODE,
  important: true 
});

const DEFAULT_AFTER_SIGN_IN_URL = import.meta.env.VITE_CLERK_AFTER_SIGN_IN_URL || "/ui";
const DEFAULT_AFTER_SIGN_UP_URL = import.meta.env.VITE_CLERK_AFTER_SIGN_UP_URL || "/ui/onboarding";

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <PostHogProvider
        apiKey={import.meta.env.VITE_APP_PUBLIC_POSTHOG_KEY}
        options={options}
      >
        <ClerkProvider
          publishableKey={PUBLISHABLE_KEY}
          signInUrl="/ui/sign-in"
          signUpUrl="/ui/sign-up"
          afterSignOutUrl={`${window.location.origin}/`}
          afterSignInUrl={`${window.location.origin}${DEFAULT_AFTER_SIGN_IN_URL}`}
          afterSignUpUrl={`${window.location.origin}${DEFAULT_AFTER_SIGN_UP_URL}`}
        >
          <QueryClientProvider client={queryClient}>
            <App />
            <Toaster /> {/* @demo: added for demo purposes */}
          </QueryClientProvider>
        </ClerkProvider>
      </PostHogProvider>
    </ErrorBoundary>
  </React.StrictMode>,
)
